import { create } from 'zustand';
import { persist } from 'zustand/middleware';

const ROTATED_LEAD_IDS_LIMIT = 20;

type CallCenterStore = {
  soundMuted: boolean;
  highlightedAssignmentId: number | null;
  rotatedLeadIds: number[];
  toggleSoundMuted: () => void;
  setHighlightedAssignmentId: (id: number | null) => void;
  addRotatedLeadId: (id: number) => void;
  removeRotatedLeadId: (id: number) => void;
};

const useCallCenterStore = create<CallCenterStore>()(
  persist(
    (set) => ({
      soundMuted: false,
      highlightedAssignmentId: null,
      rotatedLeadIds: [],

      toggleSoundMuted: () => set((state) => ({ soundMuted: !state.soundMuted })),

      setHighlightedAssignmentId: (id) => set(() => ({ highlightedAssignmentId: id })),

      addRotatedLeadId: (id) =>
        set((state) => ({
          rotatedLeadIds: [
            id,
            ...state.rotatedLeadIds.filter((existingId) => existingId !== id),
          ].slice(0, ROTATED_LEAD_IDS_LIMIT),
        })),

      // Rotation cycles leads among a small pool, so a rotated-away lead can
      // legitimately come BACK to this agent within the same session -- the
      // lead-assigned handler calls this so the stale flag can't keep the
      // moved-away banner up (and viewing pings disabled) on a lead that is
      // genuinely theirs again.
      removeRotatedLeadId: (id) =>
        set((state) => ({
          rotatedLeadIds: state.rotatedLeadIds.filter((existingId) => existingId !== id),
        })),
    }),
    {
      name: 'callcenter-storage', // name of the item in the storage (must be unique)
      // highlightedAssignmentId and rotatedLeadIds describe *this browser
      // tab's* live realtime session -- surviving a reload would make them
      // actively wrong (a stale highlight, a rotation flag for a lead that
      // has since come back), so only the mute preference is worth
      // remembering across sessions.
      partialize: (state) => ({ soundMuted: state.soundMuted }),
    },
  ),
);

export default useCallCenterStore;
